import { UserIdentityFormRendererProps } from './props';
import UserIdentityFormRendererLayout from './Layout';
import UserIdentitiesFormContent from '../Content';
import KYCUserIdentityFormKycSuccess from '../KycSuccess';

/**
 * UserIdentityFormRenderer Component
 * Renders different views based on the KYC form state and configuration.
 * Can show a success view, partial form view, or full form view with layout.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} [props.usePartialForm] - Whether to render only the form content without layout
 * @param {boolean} [props.isContainerColor] - Whether to use container background color styling
 * @param {Object} [props.kycSuccessProps] - Props for the success view component
 * @param {bigint|null} [props.externalOriginId] - External origin ID to override default origin
 * @returns {JSX.Element} The appropriate form view based on props
 *
 * @example
 * // Success view
 * <UserIdentityFormRenderer
 *   kycSuccessProps={{
 *     showSuccess: true,
 *     title: "Success",
 *     description: "KYC completed",
 *     buttonLabel: "Continue",
 *     onClick: () => {},
 *     isLoading: false
 *   }}
 * />
 *
 * // Partial form view
 * <UserIdentityFormRenderer
 *   usePartialForm={true}
 *   isContainerColor={true}
 *   externalOriginId={123n}
 * />
 *
 * // Full form view with layout
 * <UserIdentityFormRenderer
 *   isContainerColor={false}
 *   externalOriginId={null}
 * />
 */
export default function UserIdentityFormRenderer({
  usePartialForm,
  isContainerColor,
  kycSuccessProps,
  externalOriginId,
}: UserIdentityFormRendererProps) {
  if (kycSuccessProps?.showSuccess) {
    return <KYCUserIdentityFormKycSuccess {...kycSuccessProps} />;
  }

  if (usePartialForm) {
    return (
      <UserIdentitiesFormContent
        isContainerColor={isContainerColor}
        externalOriginId={externalOriginId}
      />
    );
  }

  return (
    <UserIdentityFormRendererLayout
      isContainerColor={isContainerColor}
      externalOriginId={externalOriginId}
    >
      <UserIdentitiesFormContent
        isContainerColor={isContainerColor}
        externalOriginId={externalOriginId}
      />
    </UserIdentityFormRendererLayout>
  );
}
